/**
 * tracking-context.js
 */

import React from 'react';
import { differenceInSeconds, formatISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import * as Sentry from "@sentry/react";
import { Types } from 'utils/constants';
import { uuidv4 } from 'utils/misc';
import { getTrackDistanceM } from 'utils/distance';
import { getCoordinatesViaBrowser } from 'utils/location';
import analytics from 'utils/analytics';
import { renderForMobileContext } from 'utils/app-context';
import { 
  AppMessagingContext, 
  APP_SUBSCRIPTION_TYPE, 
  APP_TRACKING_STATE 
} from 'contexts/app-messaging/app-messaging-context';
import NetworkConnectionContext from 'contexts/network-connection/network-connection.context';
import { UserContext } from 'contexts/user/user-context';
import {
  TRACK_STORAGE_KEY,
  TRACKING_STORAGE_KEY,
  TRACKING_FORM_STORAGE_KEY,
  TRACKING_STATE,
  calculateCustomPointProperties,
  getDistanceAnalyticsLabel,
  getDurationAnalyticsLabel,
} from '../tracking-common';
import { TrackContext } from '../track-context';
import { MapContext } from '../map/map-context';
import { gmapCoords } from '../map/map-common';


export const LiveTrackingContext = React.createContext();


export const LiveTrackingProvider = (props) => {
  const { 
    appInfo,
    subscribe, 
    unsubscribe, 
    setTrackingState: setAppTrackingState,
    setCompassState,
  } = React.useContext(AppMessagingContext);
  const { t } = useTranslation('general');
  const { isConnected } = React.useContext(NetworkConnectionContext);
  const { user } = React.useContext(UserContext);
  const { 
    persistenceManager, 
    tracks,
    setTracks, 
    createTrack,
    updateTrack,
    addPoint, 
    persistState,
    restoreState,
  } = React.useContext(TrackContext);
  const { map } = React.useContext(MapContext);
  const [state, setState] = React.useState({
    trackingState: TRACKING_STATE.UNSTARTED,
    isTrackingOpen: false,
    isActive: false, // trackingState: ACTIVE / STOPPED
    currentLocation: null,
    currentHeading: null,
    isUserCentered: true,
    trackCompletionAction: null,
    anonTrackCreatedCount: 0,
    isTrackHelpOpen: false,
    activeTrackWasDisconnected: false
  });
  const [isBackground, setIsBackground] = React.useState(false);
  const activeTrack = React.useMemo(() => tracks.find(t => t.isActive), [tracks]);
  // we need this ref for onLocation to know if we are active in onLocation since
  // using state.trackingState results in a dependency chain that will unsub/sub
  // to app location messages whenever the trackingState changes.
  const trackingStateRef = React.useRef(TRACKING_STATE.UNSTARTED);


  // -----------------------------------------------------------------------------------------
  // private methods
  // -----------------------------------------------------------------------------------------


  // location change handler
  const onLocation = React.useCallback((location) => {
    // format location data
    const currentLocation = {
      ...location.coords,
      dateTimeCreated: new Date(),
      activity: location.activity,
      odometer: location.odometer,
    };

    setState(p => ({ ...p, currentLocation }));
    if (trackingStateRef.current === TRACKING_STATE.ACTIVE) {
      addPoint(currentLocation);
    }
  }, [addPoint]);


  // -----------------------------------------------------------------------------------------
  // public methods
  // -----------------------------------------------------------------------------------------


  // manages drawer visibility when not in anonymous tracking mode
  const setIsTrackingOpen = React.useCallback((isTrackingOpen) => {
    setState(p => ({
      ...p,
      isTrackingOpen
    }));

    // only request browser location if running mobile site in browser
    if (isTrackingOpen && !window.ReactNativeWebView && renderForMobileContext()) {
      (async () => {
        const location = await getCoordinatesViaBrowser();
        onLocation({
          coords: {
            accuracy: location.accuracy,
            altitude: location.altitude,
            heading: location.heading,
            latitude: location.latitude,
            longitude: location.longitude,
            speed: location.speed
          }
        });
      })();
    }

    // refresh map display in case it's stale
    if (isTrackingOpen && state.currentLocation && map) {
      if (!map.center) {
        map.setCenter(gmapCoords(state.currentLocation));
        map.setZoom(20);
      } else {
        map.panBy(0, 0);
      }
    }

    setCompassState(isTrackingOpen);
  }, [onLocation, state.currentLocation, map, setCompassState]);


  // manages track help dialog visibility
  const setIsTrackHelpOpen = React.useCallback((isTrackHelpOpen) => {
    setState(p => ({
      ...p,
      isTrackHelpOpen
    }));
  }, []);


  // sets user-centered state. this will continue to follow current location if on.
  const setIsUserCentered = React.useCallback((isUserCentered) => {
    setState(p => ({
      ...p,
      isUserCentered
    }));
  }, []);


  // starts tracking
  const startTracking = React.useCallback((trackType, params, leadTrack = null) => {
    const now = new Date();
    const newTrack = {
      id: uuidv4(), // unique ID so we can find this regardless of persistence
      type: trackType,
      points: [calculateCustomPointProperties(null, null, {
        ...state.currentLocation,
        dateTimeCreated: now
      })],
      markers: [],
      latitude: state.currentLocation.latitude,
      longitude: state.currentLocation.longitude,
      params, // params unique to the track type - required for creating
      dateTimeCreated: formatISO(now), // as a string to preserve consistent timezone data (Date.toIsoString() converts to UTC)
      dateTimeStopped: null,
      isComplete: false,
      persistedPointCount: 0,
      isActive: true,
      leadTrack, // attach the lead track to the follow track so we have it when we need it
    };
    setState(p => ({
      ...p,
      trackingState: TRACKING_STATE.ACTIVE,
      isActive: true,
      activeTrackWasDisconnected: false,
    }));
    trackingStateRef.current = TRACKING_STATE.ACTIVE;
    createTrack(newTrack);
    return newTrack;
  }, [state.currentLocation, createTrack]);


  // stop tracking
  const stopTracking = React.useCallback(() => {
    setState(p => ({
      ...p,
      trackingState: TRACKING_STATE.STOPPED,
      isActive: true,
    }));
    trackingStateRef.current = TRACKING_STATE.STOPPED;
    const now = new Date();
    updateTrack(activeTrack.id, (track) => ({
      dateTimeStopped: now,
      duration: differenceInSeconds(now, track.dateTimeCreated),
      distance: getTrackDistanceM(track.points)
    }));
  }, [updateTrack, activeTrack]);


  // resume tracking
  const resumeTracking = React.useCallback(() => {
    setState(p => ({
      ...p,
      trackingState: TRACKING_STATE.ACTIVE,
      isActive: true,
    }));
    trackingStateRef.current = TRACKING_STATE.ACTIVE;
    updateTrack(activeTrack.id, {
      dateTimeStopped: null,
      duration: null,
      distance: null
    });
    addPoint(state.currentLocation);
  }, [updateTrack, activeTrack, state.currentLocation, addPoint]);


  // reset tracking.
  const resetTracking = React.useCallback((trackCompletionAction) => {    
    setState(p => ({
      ...p,
      trackingState: TRACKING_STATE.UNSTARTED,
      isActive: false,
      trackCompletionAction
    }));
    trackingStateRef.current = TRACKING_STATE.UNSTARTED;
    setTracks([]);

    // clear saved tracking data
    localStorage.removeItem(TRACK_STORAGE_KEY);
    localStorage.removeItem(TRACKING_STORAGE_KEY);
    localStorage.removeItem(TRACKING_FORM_STORAGE_KEY);
  }, [setTracks]);


  // saves active track stats
  const saveActiveTrackStats = React.useCallback(() => {
    if (activeTrack) {
      persistenceManager.saveTrackStats(activeTrack);
    }
  }, [activeTrack, persistenceManager]);


  // completes active track
  const completeActiveTrack = React.useCallback(async () => {
    if (activeTrack) {
      if (!user) {
        setState(p => ({...p, anonTrackCreatedCount: p.anonTrackCreatedCount + 1}));
      }

      // analytics
      analytics.trackGA4('event', { name: 'track_save', params: { 
        tracksave_rec_type: activeTrack.type,
        tracksave_dist_yards: getDistanceAnalyticsLabel(activeTrack.distance),
        tracksave_duration_mins: getDurationAnalyticsLabel(activeTrack.duration),
        tracksave_num_pins: activeTrack.markers.length >= 5 ? '5+' : activeTrack.markers.length,
        tracksave_was_offline: state.activeTrackWasDisconnected ? 'yes' : 'no',
      }});

      if (activeTrack.createdRecord) {
        if (activeTrack.createdRecord.recordType === Types.Record.DEPLOYMENT) {
          // new deployment created
          analytics.trackGA4('event', {
            name: 'record_save',
            params: {
              record_type: 'deployment',
              save_type: 'added',
              workmode_type: Types.WorkMode.getName(t, Types.WorkMode.PATROL),
              patrol_type: Types.Patrol.getName(t, Types.Patrol.TRACKING),
            }
          });
        } else if (activeTrack.createdRecord.recordType === Types.Record.EVENT) {
          // new event created
          analytics.trackGA4('event', { name: 'record_save', params: { record_type: 'event', save_type: 'added' } });
        }

        if (activeTrack.createdRecord.recordType === Types.Record.EVENT || activeTrack.createdRecord.recordType === Types.Record.EXERCISE) {
          // new exercise created
          analytics.trackGA4('event', {
            name: 'record_save',
            params: {
              record_type: 'exercise',
              save_type: 'added',
              workmode_type: Types.WorkMode.getName(t, Types.WorkMode.PATROL),
              patrol_type: Types.Patrol.getName(t, Types.Patrol.TRACKING),
            }
          });
        }
      }

      await persistenceManager.completeTrack(activeTrack);
    }
  }, [activeTrack, persistenceManager, state.activeTrackWasDisconnected, user, t]);


  // discard active track
  const discardActiveTrack = React.useCallback(async () => {
    if (activeTrack) {
      return await persistenceManager.discardTrack(activeTrack);
    } else {
      // there is nothing to discard so technically it succeeded?
      return true;
    }
  }, [activeTrack, persistenceManager]);



  // -----------------------------------------------------------------------------------------
  // effects (usually first but one effect depends on a public method :/ )
  // -----------------------------------------------------------------------------------------

  // INIT: get app visibility.
  // NOTE: i tried to change this to use the APP_STATE app message and it caused a strange
  // issue triggering background/foreground in a loop for about 10 seconds. Moving back
  // to this mechanism for now as the issue needs more investigation but isn't all that important
  // since this way works fine.
  React.useEffect(() => {
    const onVisibilityChange = () => {
      setIsBackground(document.hidden);
    }

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
  }, []);


  // init - app subscriptions.
  React.useEffect(() => {
    // compass heading change handler
    const onHeading = ({ accuracy, heading }) => {
      setState(p => ({ ...p, currentHeading: heading }));
    }


    if (window.ReactNativeWebView) {
      subscribe(APP_SUBSCRIPTION_TYPE.LOCATION, onLocation);
      subscribe(APP_SUBSCRIPTION_TYPE.HEADING, onHeading);
    } 

    return () => {
      unsubscribe(APP_SUBSCRIPTION_TYPE.LOCATION, onLocation);
      unsubscribe(APP_SUBSCRIPTION_TYPE.HEADING, onHeading);
      setAppTrackingState(APP_TRACKING_STATE.DISABLED);
    }
  }, [subscribe, unsubscribe, setAppTrackingState, onLocation]);


  // update persistence manager
  React.useEffect(() => {
    if (persistenceManager) {
      if (appInfo && !persistenceManager.appInfo) {
        persistenceManager.appInfo = appInfo;
      }
      if (!persistenceManager.resetTracking) {
        persistenceManager.resetTracking = resetTracking;
      }
    }
  }, [persistenceManager, appInfo, resetTracking]);


  // init = restore state
  React.useEffect(() => {
    if (renderForMobileContext() && persistenceManager) {
      const storageObjRaw = localStorage.getItem(TRACKING_STORAGE_KEY);
      const storageObj = storageObjRaw ? JSON.parse(storageObjRaw) : null;
      if (storageObj) {
        // basic sanity checks
        if (![TRACKING_STATE.ACTIVE, TRACKING_STATE.STOPPED, TRACKING_STATE.UNSTARTED].includes(storageObj.trackingState)) {
          Sentry.captureMessage(
            'Tracking context restore failed sanity check', 
            { extra: { [TRACKING_STORAGE_KEY]: JSON.stringify(storageObj) }});
          resetTracking();
          return;
        }

        setState(p => ({
          ...p,
          trackingState: storageObj.trackingState,
          isActive: [TRACKING_STATE.ACTIVE, TRACKING_STATE.STOPPED].includes(storageObj.trackingState),
          activeTrackWasDisconnected: storageObj.activeTrackWasDisconnected,
        }));
        trackingStateRef.current = storageObj.trackingState;
      }
      restoreState(resetTracking);
    }    
  }, [persistenceManager, resetTracking, restoreState]);


  // save active state to storage
  React.useEffect(() => {
    if (state.isActive && renderForMobileContext()) {
      const storageObj = {
        trackingState: state.trackingState,
        activeTrackWasDisconnected: state.activeTrackWasDisconnected,
      };

      try {
        localStorage.setItem(TRACKING_STORAGE_KEY, JSON.stringify(storageObj));
      } catch {}
      persistState();
    }
  }, [
    persistenceManager,
    persistState,
    state.isActive, 
    state.trackingState,
    state.activeTrackWasDisconnected,
  ]);
  

  // set app's tracking state based on local tracking state, open state and app bg state
  React.useEffect(() => {
    // wait until we have an initial tracking state
    if (state.trackingState === null) {
      return;
    }

    // - DISABLED: !tstate:active && (drawer:closed || app:background)
    if (state.trackingState !== TRACKING_STATE.ACTIVE && (!state.isTrackingOpen || isBackground)) {
      setAppTrackingState(APP_TRACKING_STATE.DISABLED);
    } 
    // - ENABLED: !tstate:active && drawer:open && app:foreground
    else if (state.trackingState !== TRACKING_STATE.ACTIVE && state.isTrackingOpen && !isBackground) {
      setAppTrackingState(APP_TRACKING_STATE.ENABLED);
    }
    // - ACTIVE: tstate:active 
    else if (state.trackingState === TRACKING_STATE.ACTIVE) {
      setAppTrackingState(APP_TRACKING_STATE.TRACKING);
    }
  }, [state.trackingState, state.isTrackingOpen, isBackground, setAppTrackingState]);


  // note if we go offline while tracking, for analytics.
  // TODO - consider putting this directly in the track object so we can track the value per-track
  React.useEffect(() => {
    if (!isConnected && state.trackingState === TRACKING_STATE.ACTIVE) {
      setState(p => ({
        ...p,
        activeTrackWasDisconnected: true
      }))
    }
  }, [isConnected, state.trackingState]);


  // -----------------------------------------------------------------------------------------
  // final state value
  // -----------------------------------------------------------------------------------------


  const value = React.useMemo(() => ({
    ...state,
    activeTrack,
    setIsTrackingOpen,
    setIsTrackHelpOpen,
    setIsUserCentered,
    startTracking,
    stopTracking,
    resumeTracking,
    resetTracking,
    saveActiveTrackStats,
    completeActiveTrack,
    discardActiveTrack,
  }), [
    state,
    activeTrack,
    setIsTrackingOpen,
    setIsTrackHelpOpen,
    setIsUserCentered, 
    startTracking, 
    stopTracking, 
    resumeTracking,
    resetTracking, 
    saveActiveTrackStats,
    completeActiveTrack,
    discardActiveTrack,
  ]);


  return (<>
    <LiveTrackingContext.Provider 
      value={value}
      children={props.children}
    />
  </>);
}